import { useState, useEffect, useContext, useRef } from "react";
import { AuthContext } from "../context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { mastersAPI, referenceAPI, studentsAPI, usersAPI } from "../services/api";
import StudentDeletionModal from "../components/StudentDeletionModal";
import { useTheme } from '../context/ThemeContext';
import StaffPageShell from '../components/StaffPageShell';
import EnhancedFilterForm from '../components/EnhancedFilterForm';
import StudentList from '../components/StudentList';
import StudentDirectoryLayout from '../components/StudentDirectory/StudentDirectoryLayout';
import StudentDirectoryTable from '../components/StudentDirectory/StudentDirectoryTable';
import ChangeBatchModal from '../components/ChangeBatchModal';
import ChangePasswordModal from '../components/ChangePasswordModal';
import { exportStudentsToCSV, downloadCSV } from '../utils/csvExport';
import { serializeStudentFilterParams } from '../utils/studentFilterParams';
import { useBulkStudentSelection } from '../hooks/useBulkStudentSelection';
import { useEnrollmentStatusUpdates } from '../hooks/useEnrollmentStatusUpdates';
import { useStudentInlineUpdate } from '../hooks/useStudentInlineUpdate';
import { useFilterStudentRowActions } from '../hooks/useFilterStudentRowActions';
import { useDirectoryV2Enabled } from '../hooks/useDirectoryV2Enabled';
import { useAutoApplyFilters } from '../hooks/useAutoApplyFilters';
import usePermissions from '../hooks/usePermissions';
import { BULK_DELETE_LIMIT } from '../utils/bulkStudentSelection';

const AdminFilterStudentsPage = () => {
  const { user } = useContext(AuthContext);
  const { hasPermission, role } = usePermissions();
  const canEditStudents = hasPermission('students.edit');
  const canDeleteStudents = hasPermission('students.delete');
  const isTrainer = role === 'trainer';
  const directoryV2 = useDirectoryV2Enabled();
  const token = user?.token || user?.user?.token;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentTheme } = useTheme();

  const [filters, setFilters] = useState({});
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const directoryFetchGeneration = useRef(0);
  const [expandedId, setExpandedId] = useState(null);
  const [expandedDetails, setExpandedDetails] = useState({});
  const [expandedDetailLoading, setExpandedDetailLoading] = useState(null);
  
  const {
    statusUpdateError,
    statusUpdatingIds,
    bulkStatusUpdating,
    handleEnrollmentStatusChange,
    handleBulkMarkAsLeft,
  } = useEnrollmentStatusUpdates((updater) => setStudents(updater));
  
  // Delete functionality state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteStudent, setDeleteStudent] = useState(null);
  const {
    selectedStudents,
    selectionWarning,
    clearSelection,
    toggleStudentSelection,
    selectionAtLimit,
    selectionLimit
  } = useBulkStudentSelection();

  // Dropdown master values
  const [degrees, setDegrees] = useState([]);
  const [colleges, setColleges] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [modes, setModes] = useState([]);
  const [centers, setCenters] = useState([]);
  const [grades, setGrades] = useState([]);
  const [domains, setDomains] = useState([]);

  const patchStudentInLists = (studentId, refreshedStudent) => {
    setStudents((prev) =>
      prev.map((s) => (s.id === studentId ? { ...s, ...refreshedStudent } : s))
    );
    setExpandedDetails((prev) => ({ ...prev, [studentId]: refreshedStudent }));
  };

  const {
    editingStudentId,
    savingStudentId,
    saveError: inlineSaveError,
    saveSuccess: inlineSaveSuccess,
    setSaveSuccess: setInlineSaveSuccess,
    startEdit,
    cancelEdit,
    saveStudent: saveInlineStudent,
  } = useStudentInlineUpdate({ onStudentsPatched: patchStudentInLists });

  const {
    batches: modalBatches,
    changeBatchStudent,
    passwordStudent,
    openChangeBatch,
    openChangePassword,
    closeChangeBatch,
    closeChangePassword,
    submitDirectoryPassword,
  } = useFilterStudentRowActions();

  useEffect(() => {
    const fetchMasters = async () => {
      try {
        const [
          degreesRes,
          collegesRes,
          companiesRes,
          modesRes,
          centersRes,
          gradesRes,
          domainsRes,
        ] = await Promise.all([
          mastersAPI.list('degree'),
          mastersAPI.list('college'),
          mastersAPI.list('company'),
          mastersAPI.list('trainingMode'),
          mastersAPI.list('center'),
          mastersAPI.list('trainingGrade'),
          referenceAPI.getDomains(),
        ]);
        setDegrees(
          Array.from(
            new Map((degreesRes.data || []).map(degree => [degree.value, degree])).values()
          )
        );
        setColleges(collegesRes.data || []);
        setCompanies(companiesRes.data || []);
        setModes(modesRes.data || []);
        setCenters(centersRes.data || []);
        setGrades(gradesRes.data || []);
        setDomains(Array.isArray(domainsRes?.data) ? domainsRes.data : []);
      } catch (err) {
        console.error("Error fetching master values:", err);
      }
    };
    fetchMasters();

    const studentId =
      searchParams.get('studentId') || searchParams.get('veStudentId');
    const email = searchParams.get('email');
    if (studentId || email) {
      const initial = {};
      if (studentId) initial.veStudentId = studentId.trim();
      if (email) initial.emailAddress = email.trim();
      setFilters(initial);
      fetchFilteredStudents(initial);
    } else {
      fetchAllStudents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleExpand = async (id) => {
    if (expandedId === id) {
      setExpandedId(null);
      return;
    }

    setExpandedId(id);

    if (!id || expandedDetails[id]) return;
    // Staging rows already carry list payload — no SQL student fetch.
    if (String(id).startsWith('staging:')) return;

    setExpandedDetailLoading(id);
    try {
      const res = await studentsAPI.getById(id);
      setExpandedDetails((prev) => ({ ...prev, [id]: res.data }));
    } catch (error) {
      console.error(`Error loading student details for ${id}:`, error);
    } finally {
      setExpandedDetailLoading(null);
    }
  };

  const handleChange = (e) => {
    if (e.target.name === 'clear') {
      setFilters({});
    } else if (e.target.name === 'replace') {
      setFilters(e.target.value || {});
    } else {
      setFilters({ ...filters, [e.target.name]: e.target.value });
    }
  };

  const buildFilterParams = serializeStudentFilterParams;

  const fetchFilteredStudents = async (filterOverride) => {
    const generation = ++directoryFetchGeneration.current;
    setLoading(true);
    setFetchError(null);
    try {
      const activeFilters = filterOverride ?? filters;
      const params = buildFilterParams(activeFilters);
      // Open staging only on unfiltered "show all" — filtered queries stay SQL-accurate.
      if (Object.keys(params).length === 0) {
        params.includeStaging = 'true';
      }
      const res = await usersAPI.filterStudents({ params });

      if (generation !== directoryFetchGeneration.current) return;
      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.students)
          ? res.data.students
          : [];
      setStudents(list);
    } catch (error) {
      if (generation !== directoryFetchGeneration.current) return;
      console.error('Error fetching filtered students:', error);
      setStudents([]);
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to load students';
      setFetchError(msg);
    } finally {
      if (generation === directoryFetchGeneration.current) setLoading(false);
    }
  };

  useAutoApplyFilters(directoryV2, filters, fetchFilteredStudents);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchFilteredStudents();
  };

  const handleShowAll = () => {
    // Clear all filters and fetch all students
    setFilters({});
    fetchAllStudents();
  };

  const fetchAllStudents = async () => {
    const generation = ++directoryFetchGeneration.current;
    setLoading(true);
    setFetchError(null);
    try {
      const res = await usersAPI.filterStudents({
        params: { includeStaging: 'true' },
      });
      if (generation !== directoryFetchGeneration.current) return;
      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.students)
          ? res.data.students
          : [];
      setStudents(list);
    } catch (error) {
      if (generation !== directoryFetchGeneration.current) return;
      console.error('Error fetching all students:', error);
      setStudents([]);
      const msg =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to load students';
      setFetchError(msg);
    } finally {
      if (generation === directoryFetchGeneration.current) setLoading(false);
    }
  };

  // Delete functionality handlers
  const handleOpenFullEdit = (student) => {
    const email = String(student.emailAddress || student.email || '').trim();
    const studentId = String(student.id || '').trim();
    const query = new URLSearchParams();
    if (email) query.set('email', email.toLowerCase());
    if (studentId) query.set('studentId', studentId);
    navigate(`/admin/edit-user?${query.toString()}`);
  };

  const handleDeleteStudent = (student) => {
    setDeleteStudent(student);
    setShowDeleteModal(true);
  };

  const handleBulkDelete = () => {
    if (selectedStudents.length === 0) return;
    if (selectedStudents.length > BULK_DELETE_LIMIT) return;
    setDeleteStudent(null);
    setShowDeleteModal(true);
  };

  const refreshDirectoryList = async (touchedStudent) => {
    const hasActiveFilters = Object.values(filters).some(Boolean);
    if (hasActiveFilters) {
      await fetchFilteredStudents();
    } else {
      await fetchAllStudents();
    }
    const studentId = touchedStudent?.id;
    if (studentId) {
      setExpandedDetails((prev) => {
        const next = { ...prev };
        delete next[studentId];
        return next;
      });
    }
  };

  const handleDeleteSuccess = () => {
    const hasActiveFilters = Object.values(filters).some(Boolean);
    if (hasActiveFilters) {
      fetchFilteredStudents();
    } else {
      fetchAllStudents();
    }
    clearSelection();
    setShowDeleteModal(false);
    setDeleteStudent(null);
  };

  const handleDeleteError = (err) => {
    console.error('Delete failed:', err);
  };

  // CSV Export functionality
  const handleDownloadCSV = async () => {
    try {
      // If there are filtered students, use them; otherwise fetch all
      let studentsToExport = students;
      
      if (studentsToExport.length === 0) {
        // Fetch all students for export
        setLoading(true);
        const res = await usersAPI.filterStudents({
          params: { includeStaging: 'true' },
        });
        studentsToExport = res.data.students || res.data || [];
        setLoading(false);
      }
      
      if (studentsToExport.length === 0) {
        alert('No students to export');
        return;
      }
      
      // Fetch full student details with all relationships (SQL students only)
      const studentsWithDetails = await Promise.all(
        studentsToExport
          .filter((student) => student && !student.isStaging && !String(student.id || '').startsWith('staging:'))
          .map(async (student) => {
          try {
            // Try students endpoint first
            let detailRes;
            try {
              detailRes = await studentsAPI.getById(student.id);
            } catch {
              detailRes = await api.get(`/api/users/students/${student.id}`);
            }
            return detailRes.data;
          } catch (error) {
            console.error(`Error fetching details for student ${student.id}:`, error);
            return student; // Return original if detail fetch fails
          }
        })
      );
      
      // Convert to CSV
      const csvContent = exportStudentsToCSV(studentsWithDetails);
      
      // Generate filename with timestamp
      const timestamp = new Date().toISOString().split('T')[0];
      const filename = `students_export_${timestamp}.csv`;
      
      // Download
      downloadCSV(csvContent, filename);
      
      // Show success message
      alert(`Successfully exported ${studentsWithDetails.length} student(s) to CSV`);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Error exporting CSV. Please try again.');
    }
  };

  const resultListProps = {
    students,
    expandedId,
    toggleExpand,
    expandedStudentDetails: expandedDetails,
    expandedDetailLoadingId: expandedDetailLoading,
    onDeleteStudent: canDeleteStudents ? handleDeleteStudent : undefined,
    onToggleSelection: toggleStudentSelection,
    selectedStudents,
    onBulkDelete: canDeleteStudents ? handleBulkDelete : undefined,
    showBulkActions: canDeleteStudents || canEditStudents,
    selectionLimit,
    selectionAtLimit,
    selectionWarning,
    showStatusControls: canEditStudents,
    onEnrollmentStatusChange: canEditStudents ? handleEnrollmentStatusChange : undefined,
    onBulkMarkAsLeft: canEditStudents ? () => handleBulkMarkAsLeft(selectedStudents) : undefined,
    statusUpdatingIds,
    bulkStatusUpdating,
    showInlineEdit: canEditStudents,
    canEditStudents,
    canDeleteStudents,
    editingStudentId,
    onStartEdit: canEditStudents ? startEdit : undefined,
    onCancelEdit: cancelEdit,
    onSaveInlineEdit: canEditStudents ? saveInlineStudent : undefined,
    inlineEditSavingId: savingStudentId,
    inlineEditError: inlineSaveError,
    inlineEditToken: token,
    onOpenFullEdit: canEditStudents ? handleOpenFullEdit : undefined,
    onChangeBatch: canEditStudents ? openChangeBatch : undefined,
    onChangePassword: canEditStudents ? openChangePassword : undefined,
  };

  const loadingPane = (
    <div className="text-center py-8">
      <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      <p className="mt-2 text-gray-600">Loading users...</p>
    </div>
  );

  const resultsPane = directoryV2 ? (
    loading && students.length === 0 ? (
      loadingPane
    ) : (
      <StudentDirectoryTable
        {...resultListProps}
        onHighestDegreeYearPatched={patchStudentInLists}
      />
    )
  ) : loading ? (
    loadingPane
  ) : (
    <StudentList {...resultListProps} />
  );

  const toolbar = (
    <div className={`${directoryV2 ? 'mb-4' : 'mb-6'} flex flex-wrap gap-3 items-center`}>
      {directoryV2 && loading && students.length > 0 ? (
        <span className="text-sm text-slate-600">Updating results…</span>
      ) : null}
      <button
        onClick={handleShowAll}
        disabled={loading}
        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isTrainer ? '📋 Show My Students' : '📋 Show All Records'}
      </button>
      {!directoryV2 ? (
        <button
          onClick={() => {
            setFilters({});
            fetchAllStudents();
          }}
          disabled={loading}
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          🗑️ Clear Filters
        </button>
      ) : null}
      <button
        onClick={handleDownloadCSV}
        disabled={loading}
        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
      >
        📥 Download CSV ({students.length > 0 ? students.length : 'All'} Users)
      </button>
    </div>
  );

  return (
    <StaffPageShell
      title="Filter Students"
      subtitle={
        isTrainer
          ? 'Showing only students enrolled in your linked batches'
          : undefined
      }
      wide
    >
      {isTrainer ? (
        <div className="mb-4 rounded-lg border border-[var(--staff-navy-mid,#1e4d8c)] bg-[#eef4fb] px-4 py-3 text-sm theme-text">
          As a trainer you only see students in batches linked to your account. If the list is
          empty, ask an admin to link you via <strong>Batch management → Manage trainers</strong>.
        </div>
      ) : null}

      {inlineSaveSuccess && (
        <div
          className="mb-4 p-4 rounded-lg border text-sm"
          style={{ backgroundColor: '#ecfdf5', borderColor: '#a7f3d0', color: '#065f46' }}
        >
          {inlineSaveSuccess}
          <button
            type="button"
            className="ml-3 text-xs underline"
            onClick={() => setInlineSaveSuccess(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      {inlineSaveError && (
        <div
          className="mb-4 p-4 rounded-lg border text-sm"
          style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c' }}
        >
          <strong>Update failed:</strong> {inlineSaveError}
        </div>
      )}

      {statusUpdateError && (
        <div
          className="mb-4 p-4 rounded-lg border text-sm"
          style={{ backgroundColor: '#fff7ed', borderColor: '#fed7aa', color: '#9a3412' }}
        >
          <strong>Status update failed:</strong> {statusUpdateError}
        </div>
      )}

      {fetchError && (
        <div
          className="mb-4 p-4 rounded-lg border text-sm"
          style={{ backgroundColor: '#fef2f2', borderColor: '#fecaca', color: '#b91c1c' }}
        >
          <strong>Could not load students:</strong> {fetchError}
          <span className="block mt-1 text-xs opacity-80">
            If this persists after deploy, check backend logs for <code>filterStudents failed</code>.
          </span>
        </div>
      )}

      {directoryV2 ? (
        <StudentDirectoryLayout
          sidebarProps={{
            filters,
            onChange: handleChange,
            onSubmit: handleSubmit,
            loading,
            token,
          }}
          toolbar={toolbar}
        >
          {resultsPane}
        </StudentDirectoryLayout>
      ) : (
        <>
          {toolbar}
          <EnhancedFilterForm
            filters={filters}
            onChange={handleChange}
            onSubmit={handleSubmit}
            loading={loading}
            token={token}
          />
          {resultsPane}
        </>
      )}
      
      {/* Delete Modal */}
      {canDeleteStudents && (
        <StudentDeletionModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setDeleteStudent(null);
          }}
          student={deleteStudent}
          isBulk={!deleteStudent}
          selectedStudents={selectedStudents}
          onSuccess={handleDeleteSuccess}
          onError={handleDeleteError}
        />
      )}

      {canEditStudents && (
        <>
          <ChangeBatchModal
            isOpen={!!changeBatchStudent}
            onClose={closeChangeBatch}
            student={changeBatchStudent}
            batches={modalBatches}
            onSuccess={() => refreshDirectoryList(changeBatchStudent)}
          />
          <ChangePasswordModal
            isOpen={!!passwordStudent}
            onClose={closeChangePassword}
            student={passwordStudent}
            onSubmit={submitDirectoryPassword}
          />
        </>
      )}
    </StaffPageShell>
  );
};

export default AdminFilterStudentsPage;
